import type { PropsWithChildren, ReactElement } from "react";
import { useEffect, useRef } from "react";

import { createPortal } from "react-dom";

import { IconButton } from "@similarweb/ui-components";

import { StyledCloseIconWrapper, StyledDialogOverlay, StyledDialogPanel } from "./styles";
import type { OnboardingDialogVariant } from "./variants";

type OnboardingDialogProps = {
    isOpen: boolean;
    variant: OnboardingDialogVariant;
    dataAutomation: string;
    onClose?: () => void;
};

/**
 * A plain portalled overlay rather than `BasicModal`. The React Aria modal underneath `BasicModal`
 * marks everything outside itself `inert`, which leaves third-party overlays (Appcues in
 * particular) painted on top of the modal yet unable to receive a single click. The onboarding
 * wizard is open from the moment the page loads and non-legacy users cannot dismiss it, so it
 * would swallow every in-app guide for the whole session.
 *
 * The trade-off is that this no longer traps focus or claims `aria-modal`: the page behind is
 * covered and unreachable by pointer, but a keyboard or screen-reader user can leave the dialog —
 * which is exactly what lets them reach the guides we stopped blocking.
 */
const OnboardingDialog = ({
    isOpen,
    variant,
    dataAutomation,
    onClose,
    children,
}: PropsWithChildren<OnboardingDialogProps>): ReactElement | null => {
    const panelRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (isOpen) {
            panelRef.current?.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !onClose) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    return createPortal(
        <StyledDialogOverlay
            $variant={variant}
            data-automation={`${dataAutomation}.overlay`}
        >
            <StyledDialogPanel
                ref={panelRef}
                $variant={variant}
                tabIndex={-1}
                role="dialog"
                data-automation={`${dataAutomation}.panel`}
            >
                {children}

                {onClose ? (
                    <StyledCloseIconWrapper $variant={variant}>
                        <IconButton
                            type="flat"
                            iconSize="sm"
                            iconName="clear"
                            onClick={onClose}
                            dataAutomation={`${dataAutomation}.close`}
                        />
                    </StyledCloseIconWrapper>
                ) : null}
            </StyledDialogPanel>
        </StyledDialogOverlay>,
        document.body,
    );
};

export default OnboardingDialog;
